import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import {
  LayoutDashboard,
  Scissors,
  Image,
  Star,
  CalendarDays,
  MessageSquare,
  LogOut,
  Plus,
  Trash2,
  Pencil,
  X,
  Save,
  Upload,
  Loader2,
  Bell,
  CheckCircle,
  Sparkles,
  Megaphone,
  ExternalLink,
  Copy,
  Eye,
  EyeOff,
  Send,
  Phone,
  Users,
  Mail,
  CalendarPlus,
  AlertTriangle,
  RotateCcw,
} from "lucide-react";
import { API, SITE_URL, authHeaders, resolveUrl, ImageUploadField } from "./shared";

const BookingsTab = () => {
  const [bookings, setBookings] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [view, setView] = useState("calendar"); // "calendar" or "list"
  const [sendingReminder, setSendingReminder] = useState(null);
  const [updatingStatus, setUpdatingStatus] = useState(null);

  const fetchBookings = useCallback(async () => {
    const res = await axios.get(`${API}/bookings`, authHeaders());
    setBookings(res.data);
  }, []);

  useEffect(() => { fetchBookings(); }, [fetchBookings]);

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this booking?")) return;
    try {
      await axios.delete(`${API}/admin/bookings/${id}`, authHeaders());
      toast.success("Booking deleted");
      fetchBookings();
    } catch (err) {
      toast.error("Failed to delete");
    }
  };

  const handleSendReminder = async (bookingId) => {
    setSendingReminder(bookingId);
    try {
      await axios.post(`${API}/admin/send-reminder/${bookingId}`, {}, authHeaders());
      toast.success("Reminder sent!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send reminder");
    } finally {
      setSendingReminder(null);
    }
  };

  const handleMarkPaid = async (bookingId) => {
    if (!window.confirm("Mark this booking as deposit paid and send confirmation email/SMS to client + you?")) return;
    setUpdatingStatus(bookingId);
    try {
      await axios.post(`${API}/admin/bookings/${bookingId}/mark-paid`, {}, authHeaders());
      toast.success("Marked PAID. Confirmation email + SMS sent.");
      fetchBookings();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to mark paid");
    } finally {
      setUpdatingStatus(null);
    }
  };

  const handleReconcileNow = async () => {
    setSendingReminder("__reconcile__");
    try {
      await axios.post(`${API}/admin/payments/reconcile-now`, {}, authHeaders());
      toast.success("Reconciliation triggered — refreshing in 3s");
      setTimeout(fetchBookings, 3000);
    } catch (err) {
      toast.error("Reconciliation failed");
    } finally {
      setSendingReminder(null);
    }
  };

  const handleMarkCompleted = async (bookingId) => {
    const priceInput = window.prompt("Enter final price charged (leave empty to use listed price):");
    const finalPrice = priceInput ? parseFloat(priceInput) : null;
    if (priceInput && isNaN(finalPrice)) {
      toast.error("Invalid price entered");
      return;
    }
    setUpdatingStatus(bookingId);
    try {
      await axios.put(`${API}/admin/bookings/${bookingId}/complete`, { final_price: finalPrice }, authHeaders());
      toast.success(`Completed! ${finalPrice ? `$${finalPrice} recorded.` : 'Listed price used.'} Thank you email sent.`);
      fetchBookings();
    } catch (err) {
      toast.error("Failed to update status");
    } finally {
      setUpdatingStatus(null);
    }
  };

  // Group bookings by date
  const bookingsByDate = useMemo(() => {
    const grouped = {};
    bookings.forEach((b) => {
      if (!grouped[b.date]) grouped[b.date] = [];
      grouped[b.date].push(b);
    });
    return grouped;
  }, [bookings]);

  // Get bookings for selected date
  const selectedDateStr = selectedDate.toISOString().split("T")[0];
  const dayBookings = useMemo(() => 
    (bookingsByDate[selectedDateStr] || []).slice().sort((a, b) => a.time.localeCompare(b.time)),
    [bookingsByDate, selectedDateStr]
  );

  // Dates that have bookings (for calendar highlighting)
  const bookedDates = useMemo(() => 
    Object.keys(bookingsByDate).map((d) => new Date(d + "T12:00:00")),
    [bookingsByDate]
  );

  const sortedBookings = useMemo(() =>
    bookings.slice().sort((a, b) => `${b.date}${b.time}`.localeCompare(`${a.date}${a.time}`)),
    [bookings]
  );

  // Calendar modifier for booked days
  const modifiers = { booked: bookedDates };
  const modifiersStyles = {
    booked: { fontWeight: "bold", textDecoration: "underline", textDecorationColor: "#D4AF37" },
  };

  return (
    <div data-testid="admin-bookings-tab">
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-serif text-2xl text-brand-text">Bookings</h2>
        <div className="flex gap-1">
          <button
            onClick={handleReconcileNow}
            disabled={sendingReminder === "__reconcile__"}
            data-testid="reconcile-now-btn"
            title="Ask Stripe if any pending deposits actually succeeded"
            className="font-sans text-xs px-3 py-1.5 border border-green-600 text-green-400 hover:bg-green-600/10 transition-colors disabled:opacity-50"
          >
            {sendingReminder === "__reconcile__" ? "Checking..." : "Reconcile Pending $"}
          </button>
          <button onClick={() => setView("calendar")} data-testid="view-calendar-btn" className={`font-sans text-xs px-3 py-1.5 border transition-colors ${view === "calendar" ? "border-brand-gold text-brand-gold" : "border-brand-border text-brand-text-muted"}`}>
            Calendar
          </button>
          <button onClick={() => setView("list")} data-testid="view-list-btn" className={`font-sans text-xs px-3 py-1.5 border transition-colors ${view === "list" ? "border-brand-gold text-brand-gold" : "border-brand-border text-brand-text-muted"}`}>
            All Bookings
          </button>
        </div>
      </div>

      {view === "calendar" ? (
        <div className="flex flex-col lg:flex-row gap-6">
          {/* Calendar */}
          <div className="booking-calendar flex-shrink-0">
            <Calendar
              mode="single"
              selected={selectedDate}
              onSelect={(d) => d && setSelectedDate(d)}
              modifiers={modifiers}
              modifiersStyles={modifiersStyles}
              data-testid="admin-booking-calendar"
              className="border border-brand-border bg-brand-surface p-3"
            />
            <p className="font-sans text-xs text-brand-text-muted mt-2 text-center">
              <span className="underline decoration-brand-gold">Underlined</span> dates have bookings
            </p>
          </div>

          {/* Day's Bookings */}
          <div className="flex-1">
            <h3 className="font-serif text-lg text-brand-text mb-4">
              {selectedDate.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" })}
            </h3>
            {dayBookings.length === 0 ? (
              <p className="font-sans text-sm text-brand-text-muted py-8 text-center border border-dashed border-brand-border">No bookings for this day.</p>
            ) : (
              <div className="space-y-3">
                {dayBookings.map((b) => (
                  <div key={b.id} className="p-4 border border-brand-border bg-brand-surface" data-testid={`calendar-booking-${b.id}`}>
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <span className="font-sans text-sm font-semibold text-brand-gold">{b.time}</span>
                        <span className="font-sans text-sm text-brand-text ml-2">{b.client_name}</span>
                      </div>
                      <span className={`font-sans text-[10px] px-2 py-0.5 ${b.status === "completed" ? "bg-blue-900/30 text-blue-400 border border-blue-800" : b.status === "pending" ? "bg-yellow-900/30 text-yellow-400 border border-yellow-800" : b.status === "cancelled" ? "bg-red-900/30 text-red-400 border border-red-800" : "bg-green-900/30 text-green-400 border border-green-800"}`}>{b.status}</span>
                    </div>
                    <p className="font-sans text-xs text-brand-gold mb-1">{b.service_name}</p>
                    {b.hair_type && (
                      <p className="font-sans text-[10px] text-brand-gold/90 -mt-0.5 mb-1">
                        Hair: <span className="font-semibold">{b.hair_type === "natural" ? "Natural Hair" : b.hair_type === "boho" ? "Boho Hair (extensions)" : b.hair_type}</span>
                      </p>
                    )}
                    <p className="font-sans text-xs text-brand-text-muted">{b.client_email} &middot; {b.client_phone}</p>
                    {b.deposit_paid && <p className="font-sans text-[10px] text-green-400 mt-1">💳 $25 deposit paid</p>}
                    {b.notes && <p className="font-sans text-xs text-brand-text-muted/60 mt-1">Note: {b.notes}</p>}
                    <div className="flex flex-wrap gap-2 mt-3">
                      {b.status === "pending" && !b.deposit_paid && (
                        <button
                          onClick={() => handleMarkPaid(b.id)}
                          disabled={updatingStatus === b.id}
                          data-testid={`mark-paid-${b.id}`}
                          className="flex items-center gap-1.5 px-3 py-1.5 bg-green-600 text-white font-sans text-xs font-semibold hover:bg-green-500 transition-colors disabled:opacity-50"
                        >
                          <CheckCircle size={12} />
                          {updatingStatus === b.id ? "Updating..." : "Mark Paid & Confirm"}
                        </button>
                      )}
                      {b.status === "confirmed" && (
                        <button
                          onClick={() => handleMarkCompleted(b.id)}
                          disabled={updatingStatus === b.id}
                          data-testid={`complete-booking-${b.id}`}
                          className="flex items-center gap-1.5 px-3 py-1.5 bg-brand-gold text-black font-sans text-xs font-semibold hover:bg-brand-gold-light transition-colors disabled:opacity-50"
                        >
                          <CheckCircle size={12} />
                          {updatingStatus === b.id ? "Updating..." : "Mark Completed"}
                        </button>
                      )}
                      <button
                        onClick={() => handleSendReminder(b.id)}
                        disabled={sendingReminder === b.id}
                        data-testid={`send-reminder-${b.id}`}
                        className="flex items-center gap-1.5 px-3 py-1.5 border border-brand-gold text-brand-gold font-sans text-xs hover:bg-brand-gold hover:text-black transition-colors disabled:opacity-50"
                      >
                        <Bell size={12} />
                        {sendingReminder === b.id ? "Sending..." : "Send Reminder"}
                      </button>
                      <button onClick={() => handleDelete(b.id)} data-testid={`delete-booking-${b.id}`} className="flex items-center gap-1.5 px-3 py-1.5 border border-brand-border text-brand-text-muted font-sans text-xs hover:text-red-400 hover:border-red-400 transition-colors">
                        <Trash2 size={12} /> Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      ) : (
        /* List View */
        bookings.length === 0 ? (
          <p className="font-sans text-sm text-brand-text-muted">No bookings yet.</p>
        ) : (
          <div className="space-y-3">
            {sortedBookings.map((b) => (
              <div key={b.id} className="flex items-start gap-4 p-4 border border-brand-border bg-brand-surface" data-testid={`admin-booking-${b.id}`}>
                <div className="flex-1 min-w-0">
                  <p className="font-sans text-sm font-medium text-brand-text">{b.client_name}</p>
                  <p className="font-sans text-xs text-brand-gold">{b.service_name}</p>
                  {b.hair_type && (
                    <p className="font-sans text-[10px] text-brand-gold/90">
                      Hair: <span className="font-semibold">{b.hair_type === "natural" ? "Natural Hair" : b.hair_type === "boho" ? "Boho Hair (extensions)" : b.hair_type}</span>
                    </p>
                  )}
                  <p className="font-sans text-xs text-brand-text-muted">{b.date} at {b.time}</p>
                  <p className="font-sans text-xs text-brand-text-muted">{b.client_email} &middot; {b.client_phone}</p>
                  {b.notes && <p className="font-sans text-xs text-brand-text-muted/60 mt-1">Note: {b.notes}</p>}
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  {b.status === "confirmed" && (
                    <button
                      onClick={() => handleMarkCompleted(b.id)}
                      disabled={updatingStatus === b.id}
                      data-testid={`list-complete-${b.id}`}
                      className="p-2 bg-brand-gold text-black hover:bg-brand-gold-light transition-colors"
                      title="Mark Completed & Send Thank You"
                    >
                      <CheckCircle size={14} />
                    </button>
                  )}
                  <button
                    onClick={() => handleSendReminder(b.id)}
                    disabled={sendingReminder === b.id}
                    data-testid={`list-reminder-${b.id}`}
                    className="p-2 border border-brand-gold text-brand-gold hover:bg-brand-gold hover:text-black transition-colors"
                    title="Send Reminder"
                  >
                    <Bell size={14} />
                  </button>
                  <span className={`font-sans text-xs px-2 py-1 ${b.status === "completed" ? "bg-blue-900/30 text-blue-400 border border-blue-800" : b.status === "confirmed" ? "bg-green-900/30 text-green-400 border border-green-800" : "bg-brand-surface border border-brand-border text-brand-text-muted"}`}>
                    {b.status}
                  </span>
                  <button onClick={() => handleDelete(b.id)} data-testid={`delete-booking-${b.id}`} className="p-2 border border-brand-border text-brand-text-muted hover:text-red-400 hover:border-red-400 transition-colors">
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )
      )}
    </div>
  );
};

export default BookingsTab;
